import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { client } from "../lib/api";
import { problemTitle } from "../lib/problem";
import { hasLineage, resolveRoleSignal, roleCodeResolutionOptions } from "../lib/role-codes";
import { floorOutbox } from "../areas/floor-pwa/hooks/use-floor-outbox";
import { setFloorPrincipal } from "../areas/floor-pwa/lib/floor-principal";
import { openFloorReadCache } from "../areas/floor-pwa/lib/floor-read-cache";
import { AdminLoginScreen } from "../areas/admin/screens/login";
import { isAdminHost } from "../lib/host";
import { safeNextPath } from "../lib/safe-next";
import { resetProfileRead } from "../lib/profile-locale";
import { resetRefNames } from "../lib/ref-names";
import { resetRoleFeed } from "../lib/role-codes";
import { Banner } from "../components/toast";
import { useTenantSuspension } from "../lib/tenant-suspension";
import { joinSentences } from "../lib/sentences";
import { Button } from "../components/button";

/**
 * Where a successful sign-in lands.
 *
 * THE DEFAULT IS `/home` — the operations overview. It used to be `/products`,
 * the P0 walking-skeleton table, which meant the first screen every office user
 * saw was a list whose rows were not clickable and which had no way through to a
 * product: a dead end, sitting beside a nav that also offered the real Module 01
 * catalogue. The legacy pages are retired (router.tsx keeps the paths as
 * redirects for old bookmarks); the overview is the screen that answers "what
 * needs me today" and links onward into every area.
 *
 * A tenant that is not `live` is still being set up, and the one thing its admin
 * needs is the wizard (spec 02 §3.7). `GET /v1/onboarding/session` AUTO-CREATES
 * the session, so this is one call and never a 404 — and any failure (including
 * the 403 a floor_worker gets) falls back to the overview rather than blocking
 * the sign-in.
 *
 * On a LIVE tenant a storekeeper lands on the stock lookup: spec 06 §4.1 makes
 * it "the default landing screen for the storekeeper role", and it is the only
 * screen that answers a question without an id already in hand. The role codes
 * come from the same `GET /v1/auth/tenants` feed every permission mirror uses,
 * resolved with the same host rule, so a multi-tenant user is judged by the
 * roles they hold HERE — and any failure falls through to the overview.
 */
async function landingRoute(): Promise<"/onboarding" | "/home" | "/inventory/lookup" | "/floor"> {
  let live = true;
  try {
    const { data } = await client.GET("/v1/onboarding/session");
    live = data === undefined || data.session.status === "live";
  } catch {
    live = true;
  }
  if (!live) return "/onboarding";
  try {
    const { data } = await client.GET("/v1/auth/tenants");
    // The role's LINEAGE (D-224, D-586): a tenant's renamed clone of the floor
    // worker lands where a floor worker does.
    const roles = resolveRoleSignal(
      data?.items ?? [],
      window.location.hostname,
      roleCodeResolutionOptions(),
    );
    // The floor persona FIRST. A floor_worker holds no `onboarding.view` and no
    // office nav entry, so without this they signed in on a phone, landed on the
    // office overview (the 403 fallback), and the entire module-06 floor slice
    // was reachable only by typing a URL — the same defect that was fixed once
    // for the storekeeper.
    if (hasLineage(roles, ["floor_worker"])) return "/floor";
    if (hasLineage(roles, ["storekeeper"])) return "/inventory/lookup";
  } catch {
    // fall through
  }
  return "/home";
}

/**
 * Where to go once signed in: the validated `?next=` when the sign-in was a
 * detour from the guide (spec guide §3.3), else the role's landing screen.
 * `safeNextPath` honours guide paths only, and `/guide` is not a router
 * route, so the browser loads it.
 */
function useAfterSignIn(): () => void {
  const navigate = useNavigate();
  return () => {
    const next = safeNextPath(
      new URLSearchParams(window.location.search).get("next"),
      window.location.origin,
    );
    if (next !== undefined) {
      window.location.assign(next);
      return;
    }
    void landingRoute().then((to) => navigate({ to }));
  };
}

/**
 * What a successful sign-in has to do, beyond navigating.
 *
 * 1. RECORD THE IDENTITY, so the outbox can refuse to send one operator's
 *    queued work under another's session after a shift change (floor-principal).
 * 2. UNFREEZE THE QUEUE. A 401 mid-drain latches the engine's `signedOut` flag,
 *    and sign-in is a client-side navigation — the module singleton survives it.
 *    Without this call the operator re-authenticates, the banner keeps saying
 *    "Sign in again to finish sending your work", and nothing ever sends until
 *    a hard reload.
 * 3. FORGET THE PREVIOUS PROFILE READ. It belongs to whoever used the device
 *    before, and with it their saved language (lib/profile-locale.ts).
 * 4. FORGET THE NAMES THE PREVIOUS SESSION READ (lib/ref-names.ts). They were
 *    answered under someone else's grants (spec 16 §3.13 threat 6).
 * 5. REOPEN THE FLOOR READ CACHE. Sign-out closed it so nothing still mounted
 *    behind the sign-out could write the last operator's reads back (D-635);
 *    the new session's floor screens cache their own.
 */
function signedIn(identity: string, queryClient: QueryClient): void {
  setFloorPrincipal(identity);
  floorOutbox().resumeAfterSignIn();
  openFloorReadCache();
  resetProfileRead();
  resetRefNames(queryClient);
  resetRoleFeed(queryClient);
}

/**
 * `/login` exists on BOTH kinds of host, and they are different sign-ins.
 *
 * A tenant host signs a factory user into their tenant (`/v1/auth/*`, tenant
 * resolved from the subdomain). `admin.<apex>` signs an Assessly staff member
 * into the platform console (`/v1/platform/auth/*`, mandatory TOTP, no tenant
 * at all — spec 15 §3.1). One path, one router, so the branch is here, on the
 * same host rule the root layout uses, rather than a second mechanism.
 *
 * No hook runs above the branch, so each side keeps its own hook order.
 */
export function LoginPage() {
  if (isAdminHost(window.location.hostname)) return <AdminLoginScreen />;
  return <TenantLoginPage />;
}

function TenantLoginPage() {
  const { t, i18n } = useTranslation();
  const suspension = useTenantSuspension();
  const afterSignIn = useAfterSignIn();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpRequired, setTotpRequired] = useState(false);
  const [code, setCode] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const login = useMutation({
    mutationFn: async () => {
      const { data, error } = await client.POST("/v1/auth/login", {
        body: { email, password },
      });
      if (error) throw new Error(problemTitle(error) ?? t("login.error"));
      return data;
    },
    onSuccess: (data) => {
      setErrorMsg(null);
      if (data.step === "totp") {
        setTotpRequired(true);
      } else {
        signedIn(email, queryClient);
        afterSignIn();
      }
    },
    onError: (err: unknown) => {
      setErrorMsg(err instanceof Error ? err.message : t("login.error"));
    },
  });

  const verifyTotp = useMutation({
    mutationFn: async () => {
      const { data, error } = await client.POST("/v1/auth/totp/verify", {
        body: { code },
      });
      if (error) throw new Error(problemTitle(error) ?? t("login.totpError"));
      return data;
    },
    onSuccess: () => {
      setErrorMsg(null);
      signedIn(email, queryClient);
      afterSignIn();
    },
    onError: (err: unknown) => {
      setErrorMsg(err instanceof Error ? err.message : t("login.totpError"));
    },
  });

  const pending = login.isPending || verifyTotp.isPending;

  // UX-6a: the centered-card register form ("centered card on canvas, no
  // hero" — ROADMAP §UX-6). Behavior is byte-equivalent to the pre-restyle
  // screen — same mutations, same handlers, same input ids. The e2e harness
  // reads no label here: its driver (e2e/session.ts TENANT_LOGIN_SCREEN) steers
  // by the card's data-testid, the inputs' `name` attributes and the submit
  // `type`, which e2e/unit/session-login-screen.test.ts holds to this file, and
  // an ordinary sign-in mints its session over the API without this form. The
  // English labels "Email" and "Password" and the "Sign in" button are pinned
  // by W-SKELETON's inline sign-in and by W-UX6-AUTH.
  return (
    <section className="auth-card" data-testid="tenant-login">
      <h1 className="auth-title">{t("login.title")}</h1>

      {/* The 16 S1 suspended notice (D-495 ruling 4): the login call's own
          403 says the tenant is suspended, and in which category; it replaces
          the server's English title rather than sitting beside it. */}
      {suspension.kind === "locked" ? (
        <Banner
          kind="bad"
          data-testid="tenant-suspended-notice"
          data-category={suspension.category}
        >
          {joinSentences(
            [
              t("auth.suspension.lockedNotice"),
              suspension.category === "billing"
                ? t("auth.suspension.lockedBilling")
                : t("auth.suspension.lockedOther"),
            ],
            i18n.language,
          )}
        </Banner>
      ) : suspension.kind === "purged" ? (
        <Banner kind="bad" data-testid="tenant-closed-notice">
          {t("auth.suspension.closedBody")}
        </Banner>
      ) : errorMsg !== null ? (
        <Banner kind="bad">{errorMsg}</Banner>
      ) : null}

      {!totpRequired ? (
        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            login.mutate();
          }}
        >
          <div className="ds-field">
            <label htmlFor="email">{t("login.email")}</label>
            <input
              id="email"
              name="email"
              className="ds-input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              required
            />
          </div>

          <div className="ds-field">
            <label htmlFor="password">{t("login.password")}</label>
            <input
              id="password"
              name="password"
              className="ds-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              required
            />
          </div>

          <div className="auth-actions">
            <Button
              type="submit"
              variant="primary"
              disabled={pending}
              busy={login.isPending}
              busyLabel={t("login.submitting")}
            >
              {t("login.submit")}
            </Button>
            <Link to="/forgot-password" className="ds-lnk">
              {t("login.forgot")}
            </Link>
          </div>
        </form>
      ) : (
        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            verifyTotp.mutate();
          }}
        >
          <h2 className="auth-subtitle">{t("login.totpTitle")}</h2>
          <div className="ds-field">
            <label htmlFor="totp">{t("login.totpCode")}</label>
            <input
              id="totp"
              name="totp"
              className="ds-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
              required
            />
          </div>
          <div className="auth-actions">
            <Button type="submit" variant="primary" disabled={pending} busy={verifyTotp.isPending}>
              {t("login.totpSubmit")}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
